import { isAvailableBecauseCanEditFile } from '@/app/actions';
import { Action } from '@/app/actions/actions';
import type { ActionSpecRecord } from '@/app/actions/actionsSpec';
import { sheets } from '@/app/grid/controller/Sheets';
import { currentPrintSetup } from '@/app/gridGL/interaction/pointer/PointerPageBreak';

/**
 * Which axes a break command acts on. Excel takes the selection into account:
 * a whole column only gets a vertical break, a whole row only a horizontal one,
 * and a cell gets both — never a break at the very edge of the sheet, since
 * pages already start there.
 */
const breakAxes = (): { column?: number; row?: number } => {
  const cursor = sheets.sheet.cursor;
  const { x, y } = cursor.position;
  const entireColumn = cursor.isEntireColumnSelected(x);
  const entireRow = cursor.isEntireRowSelected(y);

  return {
    ...(x > 1 && !entireRow ? { column: x } : {}),
    ...(y > 1 && !entireColumn ? { row: y } : {}),
  };
};

const hasBreakAtCursor = (): boolean => {
  const setup = sheets.sheet.printSetup;
  if (!setup) return false;
  const { column, row } = breakAxes();
  return (
    (column !== undefined && setup.colBreaks.includes(column)) || (row !== undefined && setup.rowBreaks.includes(row))
  );
};

type PageBreakActionSpec = Pick<
  ActionSpecRecord,
  Action.InsertPageBreak | Action.RemovePageBreak | Action.ResetAllPageBreaks
>;

export const pageBreakActionsSpec: PageBreakActionSpec = {
  [Action.InsertPageBreak]: {
    label: () => 'Insert Page Break',
    isAvailable: isAvailableBecauseCanEditFile,
    run: () => {
      const { column, row } = breakAxes();
      if (column === undefined && row === undefined) return;
      const setup = currentPrintSetup();
      const add = (breaks: number[], index: number | undefined) =>
        index === undefined || breaks.includes(index) ? breaks : [...breaks, index].sort((a, b) => a - b);

      sheets.sheet.setPrintSetup(
        {
          ...setup,
          colBreaks: add(setup.colBreaks, column),
          rowBreaks: add(setup.rowBreaks, row),
        },
        false
      );
    },
  },
  [Action.RemovePageBreak]: {
    label: () => 'Remove Page Break',
    isAvailable: (args) => isAvailableBecauseCanEditFile(args) && hasBreakAtCursor(),
    run: () => {
      const { column, row } = breakAxes();
      const setup = currentPrintSetup();

      sheets.sheet.setPrintSetup(
        {
          ...setup,
          colBreaks: setup.colBreaks.filter((value) => value !== column),
          rowBreaks: setup.rowBreaks.filter((value) => value !== row),
        },
        false
      );
    },
  },
  [Action.ResetAllPageBreaks]: {
    label: () => 'Reset All Page Breaks',
    isAvailable: (args) => {
      const setup = sheets.sheet.printSetup;
      return (
        isAvailableBecauseCanEditFile(args) && !!setup && (setup.colBreaks.length > 0 || setup.rowBreaks.length > 0)
      );
    },
    run: () => {
      sheets.sheet.setPrintSetup({ ...currentPrintSetup(), colBreaks: [], rowBreaks: [] }, false);
    },
  },
};
